import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface DashboardStats {
  realTimeStats: {
    activeUsers: number;
    totalUsers: number;
    totalMatches: number;
    totalMessages: number;
    revenueThisMonth: number;
  };
  topEvents: Array<{
    eventType: string;
    count: number;
  }>;
  dailyMetrics: Array<{
    date: string;
    activeUsers: number;
    newUsers: number;
    matches: number;
    messages: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_URL}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
      } else {
        setError('Failed to load dashboard');
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Two Him Platform Analytics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.realTimeStats.totalUsers || 0}
            color="bg-blue-500"
            icon="👥"
          />
          <StatCard
            title="Active Users"
            value={stats?.realTimeStats.activeUsers || 0}
            color="bg-green-500"
            icon="✨"
          />
          <StatCard
            title="Total Matches"
            value={stats?.realTimeStats.totalMatches || 0}
            color="bg-pink-500"
            icon="💕"
          />
          <StatCard
            title="Total Messages"
            value={stats?.realTimeStats.totalMessages || 0}
            color="bg-purple-500"
            icon="💬"
          />
        </div>

        {/* Revenue Card */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Revenue This Month</p>
              <p className="text-4xl font-bold mt-2">
                ${(stats?.realTimeStats.revenueThisMonth || 0).toFixed(2)}
              </p>
            </div>
            <div className="text-6xl">💰</div>
          </div>
        </div>

        {/* Top Events */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Top Events (Last 7 Days)</h2>
          <div className="space-y-3">
            {stats?.topEvents.map((event, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">{event.eventType}</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                  {event.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Metrics */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Last 30 Days Activity</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-700 font-semibold">Date</th>
                  <th className="text-right py-3 px-4 text-gray-700 font-semibold">New Users</th>
                  <th className="text-right py-3 px-4 text-gray-700 font-semibold">Active</th>
                  <th className="text-right py-3 px-4 text-gray-700 font-semibold">Matches</th>
                  <th className="text-right py-3 px-4 text-gray-700 font-semibold">Messages</th>
                </tr>
              </thead>
              <tbody>
                {stats?.dailyMetrics.slice(0, 10).map((metric, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-800">
                      {new Date(metric.date).toLocaleDateString()}
                    </td>
                    <td className="text-right py-3 px-4 text-gray-600">{metric.newUsers}</td>
                    <td className="text-right py-3 px-4 text-gray-600">{metric.activeUsers}</td>
                    <td className="text-right py-3 px-4 text-gray-600">{metric.matches}</td>
                    <td className="text-right py-3 px-4 text-gray-600">{metric.messages}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow text-left"
          >
            <div className="text-4xl mb-3">👥</div>
            <h3 className="text-lg font-bold text-gray-900">Manage Users</h3>
            <p className="text-gray-600 text-sm mt-1">View, edit, ban users</p>
          </button>

          <button
            onClick={() => navigate('/admin/moderation')}
            className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow text-left"
          >
            <div className="text-4xl mb-3">🛡️</div>
            <h3 className="text-lg font-bold text-gray-900">Moderation</h3>
            <p className="text-gray-600 text-sm mt-1">Review reports, logs</p>
          </button>

          <a
            href="http://localhost:5556"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow text-left block"
          >
            <div className="text-4xl mb-3">🗄️</div>
            <h3 className="text-lg font-bold text-gray-900">Database</h3>
            <p className="text-gray-600 text-sm mt-1">Prisma Studio</p>
          </a>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color, icon }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value.toLocaleString()}</p>
        </div>
        <div className={`${color} w-16 h-16 rounded-full flex items-center justify-center text-3xl`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
